// Admin full vehicle management — view/edit all vehicles across all hosts

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../../lib/api';
import toast from 'react-hot-toast';

const STATUS_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'approved', label: '승인됨' },
  { value: 'pending_review', label: '승인 대기' },
  { value: 'rejected', label: '거절됨' },
  { value: 'suspended', label: '일시 중지' },
  { value: 'draft', label: '작성 중' },
];

const STATUS_LABEL: Record<string, string> = {
  approved: '승인됨', pending_review: '승인 대기', rejected: '거절됨',
  suspended: '중지됨', draft: '작성 중', deleted: '삭제됨',
};

export default function AdminVehiclesAllPage() {
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [keyword, setKeyword] = useState('');
  const [meta, setMeta] = useState({ total: 0, page: 1 });

  const load = (page = 1) => {
    setLoading(true);
    const queryParams: any = { page: String(page) };
    if (status) queryParams.status = status;
    if (keyword) queryParams.keyword = keyword;

    adminApi.vehiclesAll(queryParams)
      .then((data: any) => {
        setVehicles(data.vehicles || []);
        setMeta(data.meta || { total: 0, page: 1 });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [status]);

  const formatPrice = (won: number) => new Intl.NumberFormat('ko-KR').format(won) + '원';

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">전체 차량 관리</h1>
          <p className="text-sm text-gray-500 mt-1">총 {meta.total}대 등록 · 모든 호스트의 차량을 관리합니다</p>
        </div>
        <Link to="/admin/vehicles" className="text-sm text-brand-600 hover:underline focus:outline-none focus:ring-2 focus:ring-brand-500 transition-colors duration-150">
          🚗 승인 대기 차량 보기
        </Link>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {STATUS_OPTIONS.map(o => (
          <button
            key={o.value}
            onClick={() => setStatus(o.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 ${
              status === o.value
                ? 'bg-slate-900 text-white shadow-sm'
                : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {o.label}
          </button>
        ))}
        <input
          type="text"
          className="input-field text-xs w-48 ml-auto"
          placeholder="차량명 검색..."
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load()}
        />
        <button onClick={() => load()} className="px-4 py-1.5 bg-gray-100 hover:bg-gray-200 text-xs font-medium rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500">
          검색
        </button>
      </div>

      {/* Table */}
      {vehicles.length === 0 ? (
        <div className="bg-white rounded-2xl overflow-x-auto border border-gray-100 p-12 text-center text-gray-400">
          차량이 없습니다
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl overflow-x-auto border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">차량</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">차주</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">상태</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">대여료</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">지역</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">현재</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">관리</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {vehicles.map((v: any) => (
                    <tr key={v.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-9 rounded-lg bg-gray-100 overflow-hidden shrink-0">
                            {v.primary_photo ? (
                              <img src={v.primary_photo} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-300 text-lg">🚗</div>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{v.make} {v.model}</p>
                            <p className="text-xs text-gray-400">{v.year}년식 · {v.color || '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-700">{v.owner_name}</p>
                        <p className="text-xs text-gray-400">{v.owner_email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          v.status === 'approved' ? 'bg-emerald-50 text-emerald-700' :
                          v.status === 'pending_review' ? 'bg-amber-50 text-amber-700' :
                          v.status === 'rejected' ? 'bg-red-50 text-red-600' :
                          'bg-gray-50 text-gray-600'
                        }`}>
                          {STATUS_LABEL[v.status] || v.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium">{formatPrice(v.daily_rate)}</td>
                      <td className="px-4 py-3 text-gray-500">{v.location_city}</td>
                      <td className="px-4 py-3">
                        {v.current_status === 'rented' ? (
                          <span className="text-xs text-red-600 font-medium">🔴 대여 중</span>
                        ) : v.current_status === 'available' ? (
                          <span className="text-xs text-emerald-600 font-medium">🟢 보관 중</span>
                        ) : (
                          <span className="text-xs text-gray-400">{STATUS_LABEL[v.current_status] || v.current_status}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/admin/vehicles/${v.id}/manage`}
                          className="text-xs text-brand-600 hover:underline font-medium focus:outline-none focus:ring-2 focus:ring-brand-500 transition-colors duration-150"
                        >
                          정보 수정
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {meta.total > 50 && (
            <div className="flex items-center justify-between mt-4 text-sm">
              <span className="text-gray-400">
                총 {meta.total}대 중 {(meta.page - 1) * 50 + 1}~{Math.min(meta.page * 50, meta.total)}대
              </span>
              <div className="flex gap-1">
                {meta.page > 1 && (
                  <button onClick={() => load(meta.page - 1)}
                    className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500">
                    ← 이전
                  </button>
                )}
                {Array.from({ length: Math.min(Math.ceil(meta.total / 50), 10) }, (_, i) => {
                  const p = i + Math.max(1, meta.page - 4);
                  if (p > Math.ceil(meta.total / 50)) return null;
                  return (
                    <button key={p} onClick={() => load(p)}
                      className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                        p === meta.page ? 'bg-slate-900 text-white' : 'bg-white border border-gray-200 hover:bg-gray-50'
                      }`}>
                      {p}
                    </button>
                  );
                })}
                {meta.page < Math.ceil(meta.total / 50) && (
                  <button onClick={() => load(meta.page + 1)}
                    className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500">
                    다음 →
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
